import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Account, NewsletterFrequency } from '../models/account';
import { User } from '../models/user';
import { AuthService } from './auth.service';

// Paramètres du compte de l'utilisateur connecté (API /account, protégée par JWT)
@Injectable({
  providedIn: 'root'
})
export class AccountService {

  private host = environment.host + '/account';

  private account = new BehaviorSubject<Account | null>(null);
  account$ = this.account.asObservable();

  constructor(private httpClient: HttpClient, private authService: AuthService) {
    this.authService.loggedIn$.subscribe(loggedIn => {
      if (!loggedIn) {
        this.account.next(null);
      }
    });
  }

  get accountValue(): Account | null {
    return this.account.value;
  }

  load(): Observable<Account> {
    return this.httpClient.get<Account>(this.host).pipe(tap(account => this.account.next(account)));
  }

  updateProfile(changes: { pseudo?: string; description?: string }): Observable<User> {
    return this.refreshSession(this.httpClient.put<User>(this.host + '/profile', changes));
  }

  updateAvatar(avatar: string): Observable<User> {
    return this.refreshSession(this.httpClient.put<User>(this.host + '/avatar', { avatar }));
  }

  removeAvatar(): Observable<User> {
    return this.refreshSession(this.httpClient.delete<User>(this.host + '/avatar'));
  }

  updateEmail(email: string, password: string): Observable<User> {
    return this.refreshSession(this.httpClient.put<User>(this.host + '/email', { email, password }));
  }

  updatePassword(currentPassword: string, newPassword: string): Observable<{ updated: boolean }> {
    return this.httpClient.put<{ updated: boolean }>(this.host + '/password', { currentPassword, newPassword });
  }

  updatePreferences(preferences: { [key: string]: boolean | string }): Observable<Account> {
    return this.store(this.httpClient.put<Account>(this.host + '/preferences', preferences));
  }

  updateNotificationSettings(settings: { [key: string]: boolean }): Observable<Account> {
    return this.store(this.httpClient.put<Account>(this.host + '/notifications', settings));
  }

  // Newsletter et offres des partenaires : deux consentements distincts, un champ absent reste inchangé
  updateNewsletter(choice: { subscribed?: boolean; frequency?: NewsletterFrequency; partnerOffers?: boolean }): Observable<Account> {
    return this.store(this.httpClient.put<Account>(this.host + '/newsletter', choice));
  }

  exportData(): Observable<object> {
    return this.httpClient.get<object>(this.host + '/export');
  }

  deleteAccount(password: string): Observable<{ deleted: boolean }> {
    return this.httpClient.post<{ deleted: boolean }>(this.host + '/delete', { password }).pipe(
      tap(() => this.authService.logout())
    );
  }

  follow(userId: string): Observable<Account> {
    return this.store(this.httpClient.post<Account>(this.host + '/following/' + userId, {}));
  }

  ignore(userId: string): Observable<Account> {
    return this.store(this.httpClient.post<Account>(this.host + '/ignored/' + userId, {}));
  }

  unfollow(ids: string[]): Observable<Account> {
    return this.store(this.httpClient.post<Account>(this.host + '/following/remove', { ids }));
  }

  unignore(ids: string[]): Observable<Account> {
    return this.store(this.httpClient.post<Account>(this.host + '/ignored/remove', { ids }));
  }

  private store(request: Observable<Account>): Observable<Account> {
    return request.pipe(tap(account => this.account.next(account)));
  }

  // Le backend renvoie l'utilisateur avec un nouveau token (pseudo/email/avatar ont pu changer)
  private refreshSession(request: Observable<User>): Observable<User> {
    return request.pipe(tap(user => {
      if (user && user.access_token) {
        this.authService.persistSession(user);
      }
    }));
  }
}
